import React, { useMemo } from 'react';
import { CheckCircle, AlertTriangle, AlertCircle, TrendingUp } from 'lucide-react';

const LiveDraft = ({
  sections = [],
  sectionResponses = {},
  currentStepIndex = 0,
  wordGoal = 650,
  qualityIndicators = []
}) => {
  // Calculate total word count
  const totalWordCount = useMemo(() => {
    if (!sectionResponses || typeof sectionResponses !== 'object') {
      return 0;
    }
    return Object.values(sectionResponses).reduce((total, text) => {
      const words = text && text.trim ? text.trim().split(/\s+/).length : 0;
      return total + words;
    }, 0);
  }, [sectionResponses]);

  // Compile essay sections
  const essaySections = useMemo(() => {
    if (!sections || !Array.isArray(sections)) {
      return [];
    }
    return sections.map((section, index) => ({
      id: section.id,
      label: section.label,
      text: (sectionResponses && sectionResponses[section.id]) || '',
      isActive: index === currentStepIndex,
      isEmpty: !(sectionResponses && sectionResponses[section.id]),
      index
    }));
  }, [sections, sectionResponses, currentStepIndex]);

  // Get quality badge styling
  const getQualityBadgeClass = (indicator) => {
    switch (indicator.status) {
      case 'good':
        return 'badge-good';
      case 'warning':
        return 'badge-warning';
      case 'needs-work':
        return 'badge-needs-work';
      default:
        return 'badge-neutral';
    }
  };

  const getQualityIcon = (status) => {
    switch (status) {
      case 'good':
        return '✓';
      case 'warning':
        return '⚠';
      case 'needs-work':
        return '○';
      default:
        return '•';
    }
  };

  // Default quality indicators if none provided
  const displayIndicators = qualityIndicators.length > 0 ? qualityIndicators : [
    { label: 'Strong opening', status: totalWordCount > 50 ? 'good' : 'neutral' },
    { label: 'Personal voice', status: totalWordCount > 100 ? 'good' : 'neutral' },
    { label: 'Add details', status: totalWordCount > 200 ? 'warning' : 'neutral' },
    { label: 'Flow & transitions', status: totalWordCount > 300 ? 'needs-work' : 'neutral' }
  ];

  return (
    <div className="draft-column">
      {/* Header */}
      <div className="draft-header">
        <div className="draft-title-row">
          <h3 className="draft-title">LIVE DRAFT</h3>
          <div className="word-counter">
            <div className="word-number">{totalWordCount}</div>
            <div className="word-target">/{wordGoal} words</div>
          </div>
        </div>
        
        <p className="draft-subtitle">Your essay builds as you write.</p>
        
        <div className="quality-indicators">
          {displayIndicators.map((indicator, index) => (
            <div 
              key={index} 
              className={`quality-badge ${getQualityBadgeClass(indicator)}`}
            >
              <span>{getQualityIcon(indicator.status)}</span>
              {indicator.label}
            </div>
          ))}
        </div>
      </div>

      {/* Draft Content */}
      <div className="draft-content">
        <div className="draft-text">
          {essaySections.map((section) => {
            // Skip empty sections that aren't active
            if (section.isEmpty && !section.isActive) {
              // Show placeholder for next section
              if (section.index === currentStepIndex + 1) {
                return (
                  <div 
                    key={section.id} 
                    className="draft-section draft-placeholder"
                  >
                  </div>
                );
              }
              return null;
            }

            return (
              <div 
                key={section.id} 
                className={`draft-section ${section.isActive ? 'current-section' : ''}`}
              >
                {section.text || (
                  <em className="empty-text">
                    Writing {section.label}...
                  </em>
                )}
              </div>
            );
          })}
          

        </div>
      </div>
    </div>
  );
};

export default LiveDraft;